import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Platform,
  Alert,
  ActivityIndicator,
  KeyboardAvoidingView,
  Pressable,
} from 'react-native';
import { StatusBar } from 'expo-status-bar';
import * as ImagePicker from 'expo-image-picker';
import { RichEditor, RichToolbar, actions } from 'react-native-pell-rich-editor';
import { useTheme } from '../context/ThemeContext';
import HamburgerMenu from '../components/HamburgerMenu';
import ProfileIcon from '../components/ProfileIcon';
import Stopwatch from '../components/Stopwatch';
import { getApiUrl } from '../utils/apiConfig';
import { dedupeCoursesForDropdown } from '../utils/coursesCatalog';
import {
  getSupabaseClient,
  getStoryImagesBucketName,
  getSupabaseConfigHint,
} from '../utils/supabase';

const STORY_TOAST = {
  success: 'The story has been successfully saved',
  fail: 'The story has failed to save. Please try again or contact your admin.',
  noCourse: 'No course is selected',
};

function sanitizeStoryFilename(name) {
  const raw = String(name || '').replace(/^.*[/\\]/, '');
  const cleaned = raw.replace(/[^a-zA-Z0-9._-]+/g, '_').replace(/^_+|_+$/g, '');
  return (cleaned || 'image').slice(0, 180);
}

function guessMimeFromName(name) {
  const lower = String(name).toLowerCase();
  if (lower.endsWith('.png')) return 'image/png';
  if (lower.endsWith('.webp')) return 'image/webp';
  if (lower.endsWith('.gif')) return 'image/gif';
  if (lower.endsWith('.heic')) return 'image/heic';
  if (lower.endsWith('.jpeg')) return 'image/jpeg';
  return 'image/jpeg';
}

function isHtmlEffectivelyEmpty(html) {
  const t = String(html || '')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .trim();
  return t.length === 0;
}

async function uriToArrayBuffer(uri) {
  const res = await fetch(uri);
  if (!res.ok) throw new Error(`Could not read image (${res.status})`);
  return res.arrayBuffer();
}

/** Escape and wrap plain text as HTML for `description` (course story viewer expects HTML or legacy bullets). */
function plainTextToStoryHtml(text) {
  const raw = String(text || '');
  const trimmed = raw.trim();
  if (!trimmed) return '';
  const escaped = raw
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  const blocks = escaped.split(/\n\s*\n/);
  if (blocks.length === 1) {
    return `<p>${escaped.trim().replace(/\r?\n/g, '<br/>')}</p>`;
  }
  return blocks
    .map((b) => b.trim())
    .filter(Boolean)
    .map((b) => `<p>${b.replace(/\r?\n/g, '<br/>')}</p>`)
    .join('');
}

export default function StoryBuilderPage({ navigation, route }) {
  const theme = useTheme();
  const stopwatchRef = useRef(null);
  const richRef = useRef(null);
  const [currentUser, setCurrentUser] = useState(route?.params?.user ?? null);
  /** Plain body on web (RichEditor uses WebView, unsupported on RN Web). Native keeps HTML from RichEditor. */
  const [plainDescription, setPlainDescription] = useState('');

  const [courses, setCourses] = useState([]);
  const [courseDropdownOpen, setCourseDropdownOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);

  const [storyTitle, setStoryTitle] = useState('');
  const [pendingImages, setPendingImages] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  /** Toast after submit: { variant, message } or null */
  const [submitBanner, setSubmitBanner] = useState(null);

  const fetchUser = useCallback(async (userId) => {
    try {
      const response = await fetch(`${getApiUrl()}/api/users/${userId}`);
      if (response.ok) {
        const data = await response.json();
        setCurrentUser(data);
      }
    } catch (e) {
      console.error('StoryBuilder fetchUser:', e);
    }
  }, []);

  useEffect(() => {
    const uid = route?.params?.userId ?? route?.params?.user?.id ?? currentUser?.id;
    if (uid != null && !route?.params?.user) {
      fetchUser(uid);
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${getApiUrl()}/api/courses`);
        if (!r.ok) return;
        const data = await r.json();
        if (!cancelled && Array.isArray(data)) setCourses(dedupeCoursesForDropdown(data));
      } catch (e) {
        console.error('StoryBuilder courses:', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [route?.params?.userId, route?.params?.user, fetchUser, currentUser?.id]);

  /** Deep-link from course detail: pre-select course once catalog loads */
  useEffect(() => {
    const initialId = route?.params?.initialCourseId;
    if (initialId == null || courses.length === 0) return;
    const match = courses.find((c) => Number(c.id) === Number(initialId));
    if (match) {
      setSelectedCourse(match);
      setCourseDropdownOpen(false);
    }
  }, [courses, route?.params?.initialCourseId]);

  const resetFormFields = useCallback(() => {
    setSelectedCourse(null);
    setCourseDropdownOpen(false);
    setStoryTitle('');
    setPendingImages([]);
    setPlainDescription('');
    richRef.current?.setContentHTML('');
  }, []);

  const resetForm = useCallback(() => {
    resetFormFields();
    setSubmitBanner(null);
  }, [resetFormFields]);

  const handleClear = () => {
    resetForm();
  };

  const handlePickImages = async () => {
    try {
      const perm = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (!perm.granted) {
        Alert.alert('Permission needed', 'Allow photo library access to attach images.');
        return;
      }
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsMultipleSelection: true,
        quality: 0.85,
      });
      if (result.canceled) return;
      const assets = result.assets || [];
      const batch = assets.map((a, i) => ({
        id: `${Date.now()}-${i}-${Math.random().toString(36).slice(2, 9)}`,
        uri: a.uri,
        fileName: a.fileName || a.uri.split(/[/\\]/).pop() || `image_${i}.jpg`,
      }));
      setPendingImages((prev) => [...prev, ...batch]);
    } catch (e) {
      console.error(e);
      Alert.alert('Image picker', e.message || 'Could not open photo library.');
    }
  };

  const removePendingImage = (id) => {
    setPendingImages((prev) => prev.filter((p) => p.id !== id));
  };

  const handleSubmit = async () => {
    console.log('[StoryBuilder] Submit started', {
      platform: Platform.OS,
      apiUrl: getApiUrl(),
    });
    setSubmitBanner(null);

    const studentId = currentUser?.id ?? route?.params?.userId;
    if (studentId == null || Number.isNaN(Number(studentId))) {
      console.warn('[StoryBuilder] Blocked: no student id', {
        currentUserId: currentUser?.id,
        routeUserId: route?.params?.userId,
      });
      setSubmitBanner({ variant: 'error', message: STORY_TOAST.fail });
      return;
    }
    if (!selectedCourse) {
      console.warn('[StoryBuilder] Blocked: no course selected');
      setSubmitBanner({ variant: 'error', message: STORY_TOAST.noCourse });
      return;
    }

    let html = '';
    if (Platform.OS === 'web') {
      html = plainTextToStoryHtml(plainDescription);
    } else {
      try {
        html = (await richRef.current?.getContentHtml?.()) || '';
      } catch (e) {
        console.warn('[StoryBuilder] getContentHtml failed', e);
        html = '';
      }
    }
    if (Platform.OS === 'web') {
      if (!String(plainDescription || '').trim()) {
        console.warn('[StoryBuilder] Blocked: empty description (web plain text)');
        setSubmitBanner({ variant: 'error', message: STORY_TOAST.fail });
        return;
      }
    } else if (isHtmlEffectivelyEmpty(html)) {
      console.warn('[StoryBuilder] Blocked: empty description (rich editor)');
      setSubmitBanner({ variant: 'error', message: STORY_TOAST.fail });
      return;
    }

    console.log('[StoryBuilder] Validation OK', {
      studentId,
      courseId: selectedCourse.id,
      courseName: selectedCourse.course_name,
      storyTitle: storyTitle.trim() || null,
      descriptionChars: html.length,
      pendingImages: pendingImages.length,
    });

    const pendingCount = pendingImages.length;

    const executeSubmit = async (skipImageUpload) => {
      setSubmitting(true);
      let storyId = null;
      try {
        const supabase =
          !skipImageUpload && pendingCount > 0 ? getSupabaseClient() : null;

        const postUrl = `${getApiUrl()}/api/stories`;
        const postBody = {
          author_student_id: Number(studentId),
          story_title: storyTitle.trim() || null,
          description: html,
          course_id: selectedCourse.id,
        };
        console.log('[StoryBuilder] POST /api/stories …', postUrl, {
          skipImageUpload,
        });

        const createRes = await fetch(postUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(postBody),
        });

        const rawCreateText = await createRes.text();
        console.log('[StoryBuilder] POST response', {
          ok: createRes.ok,
          status: createRes.status,
          bodyPreview: rawCreateText.slice(0, 500),
        });

        let created = {};
        try {
          created = rawCreateText ? JSON.parse(rawCreateText) : {};
        } catch (parseErr) {
          console.warn('[StoryBuilder] POST body JSON parse failed', parseErr);
        }

        if (!createRes.ok) {
          throw new Error(created.error || `Save failed (${createRes.status})`);
        }
        storyId = created.id;
        console.log('[StoryBuilder] Story row created', {
          storyId,
          createdKeys: Object.keys(created),
        });

        if (!skipImageUpload && pendingCount > 0 && supabase) {
          const bucket = getStoryImagesBucketName();
          const urls = [];
          for (let i = 0; i < pendingImages.length; i += 1) {
            const img = pendingImages[i];
            const safe = sanitizeStoryFilename(img.fileName);
            const path = `${storyId}_${safe}`;
            console.log('[StoryBuilder] Uploading image', {
              index: i + 1,
              of: pendingCount,
              path,
              fileName: img.fileName,
            });
            const body = await uriToArrayBuffer(img.uri);
            const { error } = await supabase.storage.from(bucket).upload(path, body, {
              contentType: guessMimeFromName(img.fileName),
              upsert: false,
            });
            if (error) {
              console.warn('[StoryBuilder] Storage upload error', { path, error });
              throw new Error(error.message || 'Storage upload failed');
            }
            const { data: pub } = supabase.storage.from(bucket).getPublicUrl(path);
            urls.push(pub.publicUrl);
            console.log('[StoryBuilder] Uploaded OK', { path, publicUrl: pub.publicUrl });
          }

          const patchUrl = `${getApiUrl()}/api/stories/${storyId}`;
          console.log('[StoryBuilder] PATCH story_images …', patchUrl, {
            urlCount: urls.length,
          });

          const patchRes = await fetch(patchUrl, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ story_images: urls }),
          });
          const rawPatchText = await patchRes.text();
          console.log('[StoryBuilder] PATCH response', {
            ok: patchRes.ok,
            status: patchRes.status,
            bodyPreview: rawPatchText.slice(0, 400),
          });

          let patched = {};
          try {
            patched = rawPatchText ? JSON.parse(rawPatchText) : {};
          } catch (pe) {
            console.warn('[StoryBuilder] PATCH body JSON parse failed', pe);
          }
          if (!patchRes.ok) {
            throw new Error(
              patched.error || `Could not save image URLs (${patchRes.status})`
            );
          }
          console.log('[StoryBuilder] story_images saved', { storyId });
        }

        console.log('[StoryBuilder] Success — banner & resetting fields');
        resetFormFields();
        setSubmitBanner({ variant: 'success', message: STORY_TOAST.success });
      } catch (err) {
        console.error('[StoryBuilder] Submit failed', err);
        console.warn('[StoryBuilder] Error detail', err?.message || String(err));
        if (storyId != null) {
          console.log('[StoryBuilder] Rolling back story row', { storyId });
          try {
            const delRes = await fetch(`${getApiUrl()}/api/stories/${storyId}`, {
              method: 'DELETE',
            });
            console.log('[StoryBuilder] DELETE rollback response', {
              ok: delRes.ok,
              status: delRes.status,
            });
          } catch (delErr) {
            console.warn('[StoryBuilder] DELETE rollback failed', delErr);
          }
        }
        setSubmitBanner({ variant: 'error', message: STORY_TOAST.fail });
      } finally {
        console.log('[StoryBuilder] Submit finished (submitting=false)');
        setSubmitting(false);
      }
    };

    if (pendingCount > 0) {
      const supabaseClient = getSupabaseClient();
      const missing = getSupabaseConfigHint();
      if (missing.length || !supabaseClient) {
        console.warn('[StoryBuilder] Supabase not configured — offering save without images', {
          missingEnv: missing,
          hasClient: !!supabaseClient,
        });
        Alert.alert(
          'Images unavailable',
          'Supabase is not configured (missing URL or anon key). You can save the story without photos, or add EXPO_PUBLIC_SUPABASE_URL / EXPO_PUBLIC_SUPABASE_ANON_KEY or expo.extra keys in app.json and reload.',
          [
            { text: 'Cancel', style: 'cancel' },
            {
              text: 'Save without images',
              onPress: () => {
                void executeSubmit(true);
              },
            },
          ]
        );
        return;
      }
      console.log('[StoryBuilder] Supabase client ready', {
        bucket: getStoryImagesBucketName(),
      });
    }

    await executeSubmit(false);
  };

  const styles = getStyles(theme);
  const editorBg = theme.isDarkMode ? theme.colors.surface : '#F5F5F5';
  const editorFg = theme.colors.text;

  const toolbarActions = [
    actions.setBold,
    actions.setItalic,
    actions.insertBulletsList,
    actions.insertOrderedList,
    actions.heading2,
    actions.heading3,
    actions.fontSize,
    actions.removeFormat,
  ];

  const isWeb = Platform.OS === 'web';

  return (
    <View style={styles.flexFill} {...(isWeb ? { dataSet: { screen: 'story-builder' } } : {})}>
      <KeyboardAvoidingView
        style={styles.container}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        keyboardVerticalOffset={Platform.OS === 'ios' ? 64 : 0}
      >
      <StatusBar style={theme.isDarkMode ? 'light' : 'dark'} />

      <View style={styles.header}>
        <View style={styles.hamburgerContainer}>
          <HamburgerMenu navigation={navigation} stopwatchRef={stopwatchRef} currentUser={currentUser} />
        </View>
        <View style={styles.breadcrumbContainer}>
          <Text style={styles.breadcrumb}>Story Builder</Text>
        </View>
        <View style={styles.profileIconContainer}>
          <ProfileIcon navigation={navigation} user={currentUser} />
        </View>
      </View>

      {submitBanner != null && (
        <View
          style={[
            styles.submitBannerOuter,
            submitBanner.variant === 'success'
              ? styles.submitBannerSuccessOuter
              : styles.submitBannerErrorOuter,
          ]}
          accessibilityRole="alert"
        >
          <Text style={styles.submitBannerMessage}>{submitBanner.message}</Text>
          <TouchableOpacity
            style={[
              styles.submitBannerClose,
              theme.isDarkMode ? styles.submitBannerCloseDark : styles.submitBannerCloseLight,
            ]}
            onPress={() => setSubmitBanner(null)}
            hitSlop={{ top: 14, bottom: 14, left: 14, right: 14 }}
            accessibilityRole="button"
            accessibilityLabel="Dismiss notification"
          >
            <Text style={styles.submitBannerCloseIcon}>✕</Text>
          </TouchableOpacity>
        </View>
      )}

      <ScrollView
        style={styles.content}
        contentContainerStyle={styles.contentContainer}
        keyboardShouldPersistTaps="handled"
        nestedScrollEnabled
      >
        <Text style={styles.title}>Story Builder</Text>
        <Text style={styles.subtitle}>
          Link a course, add a title and images, then write your story.
          {isWeb
            ? ' On web, use plain text (line breaks and blank lines become paragraphs on the course page).'
            : ' Formatting uses HTML under the hood so it displays correctly on course pages.'}
        </Text>

        <View style={styles.formField}>
          <Text style={styles.label}>Select Associated Course</Text>
          <View style={styles.storyDropdownShell}>
            <Pressable
              style={styles.storyDropdownInner}
              onPress={() => setCourseDropdownOpen((o) => !o)}
              {...(isWeb ? { dataSet: { storyBuilderCourseTrigger: 'true' } } : {})}
            >
              <Text style={styles.dropdownTriggerText} numberOfLines={1}>
                {selectedCourse ? selectedCourse.course_name : 'Select Associated Course'}
              </Text>
              <Text style={styles.dropdownChevron}>{courseDropdownOpen ? '▲' : '▼'}</Text>
            </Pressable>
          </View>
          {courseDropdownOpen && (
            <View
              style={styles.dropdownList}
              {...(isWeb ? { dataSet: { dropdownSurface: 'true' } } : {})}
            >
              <ScrollView style={styles.dropdownScroll} nestedScrollEnabled keyboardShouldPersistTaps="handled">
                {courses.map((c) => (
                  <TouchableOpacity
                    key={String(c.id)}
                    style={styles.dropdownItem}
                    onPress={() => {
                      setSelectedCourse(c);
                      setCourseDropdownOpen(false);
                    }}
                    {...(isWeb ? { dataSet: { dropdownRow: 'true' } } : {})}
                  >
                    <Text style={styles.dropdownItemText}>{c.course_name}</Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
            </View>
          )}
        </View>

        <View style={styles.formField}>
          <Text style={styles.label}>Story Title</Text>
          <TextInput
            style={styles.input}
            value={storyTitle}
            onChangeText={setStoryTitle}
            placeholder="Story title"
            placeholderTextColor={theme.isDarkMode ? '#888' : '#999'}
          />
        </View>

        <View style={styles.formField}>
          <Text style={styles.label}>Images</Text>
          <TouchableOpacity style={styles.uploadButton} onPress={handlePickImages} activeOpacity={0.8}>
            <Text style={styles.uploadButtonText}>Upload image(s)</Text>
          </TouchableOpacity>
          {pendingImages.map((img) => (
            <View
              key={img.id}
              style={styles.pendingRow}
              {...(isWeb ? { dataSet: { storyBuilderPendingFileRow: 'true' } } : {})}
            >
              <Text style={styles.pendingName} numberOfLines={1}>
                {img.fileName}
              </Text>
              <TouchableOpacity onPress={() => removePendingImage(img.id)} hitSlop={12}>
                <Text style={styles.pendingRemove}>✕</Text>
              </TouchableOpacity>
            </View>
          ))}
        </View>

        <View style={styles.formField}>
          <Text style={styles.label}>Story description</Text>
          {isWeb ? (
            <>
              <Text style={styles.helpText}>
                Plain text only on web. Blank lines separate paragraphs; single line breaks become line breaks on the
                course page.
              </Text>
              <TextInput
                style={[styles.input, styles.descriptionTextArea]}
                multiline
                value={plainDescription}
                onChangeText={setPlainDescription}
                placeholder="Write your story..."
                placeholderTextColor={theme.isDarkMode ? '#888' : '#999'}
                textAlignVertical="top"
              />
            </>
          ) : (
            <>
              <Text style={styles.helpText}>
                Bold, headings, bullets and numbering are available in the toolbar.
              </Text>
              <RichToolbar
                editor={richRef}
                actions={toolbarActions}
                style={styles.richToolbar}
                iconTint={theme.isDarkMode ? '#ccc' : '#555'}
                selectedIconTint={theme.colors.primary || '#d4af37'}
                iconSize={22}
              />
              <View style={[styles.editorShell, { borderColor: theme.colors.border }]}>
                <RichEditor
                  ref={richRef}
                  placeholder="Write your story..."
                  initialHeight={220}
                  useContainer={false}
                  editorStyle={{
                    backgroundColor: editorBg,
                    color: editorFg,
                    placeholderColor: theme.isDarkMode ? '#888' : '#999',
                    contentCSSText: `font-size: 15px; line-height: 1.45; color: ${editorFg};`,
                  }}
                  style={styles.richEditor}
                />
              </View>
            </>
          )}
        </View>

        <View style={styles.buttonRow}>
          <TouchableOpacity
            style={[styles.button, styles.clearButton]}
            onPress={handleClear}
            disabled={submitting}
          >
            <Text style={styles.clearButtonText}>Clear</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.button, styles.submitButton]}
            onPress={() => {
              console.log('[StoryBuilder] Submit button pressed');
              handleSubmit();
            }}
            disabled={submitting}
          >
            {submitting ? (
              <ActivityIndicator color="#000" />
            ) : (
              <Text style={styles.submitButtonText}>Submit</Text>
            )}
          </TouchableOpacity>
        </View>

      </ScrollView>

      <Stopwatch ref={stopwatchRef} />
    </KeyboardAvoidingView>
    </View>
  );
}

const getStyles = (theme) =>
  StyleSheet.create({
    flexFill: {
      flex: 1,
    },
    container: {
      flex: 1,
      backgroundColor: theme.isDarkMode ? theme.colors.background : '#FFFFFF',
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingTop: 50,
      paddingBottom: 20,
      paddingHorizontal: 20,
      backgroundColor: 'transparent',
    },
    hamburgerContainer: {
      marginRight: 15,
    },
    breadcrumbContainer: {
      flex: 1,
    },
    breadcrumb: {
      fontSize: 18,
      fontWeight: '600',
      color: theme.colors.heading,
    },
    profileIconContainer: {
      backgroundColor: 'transparent',
    },
    submitBannerOuter: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      marginHorizontal: 20,
      marginBottom: 10,
      borderRadius: 8,
      paddingVertical: 12,
      paddingLeft: 16,
      paddingRight: 10,
      borderWidth: 1,
    },
    submitBannerSuccessOuter: {
      backgroundColor: '#198754',
      borderColor: theme.isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.12)',
    },
    submitBannerErrorOuter: {
      backgroundColor: '#DC3545',
      borderColor: theme.isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.12)',
    },
    submitBannerMessage: {
      flex: 1,
      flexShrink: 1,
      color: '#FFFFFF',
      fontSize: 16,
      fontWeight: '600',
      lineHeight: 22,
      paddingRight: 8,
      paddingTop: 2,
    },
    submitBannerClose: {
      alignSelf: 'flex-start',
      marginTop: 0,
      paddingHorizontal: 10,
      paddingVertical: 6,
      borderRadius: 8,
    },
    submitBannerCloseDark: {
      backgroundColor: 'rgba(0,0,0,0.22)',
    },
    submitBannerCloseLight: {
      backgroundColor: 'rgba(255,255,255,0.22)',
    },
    submitBannerCloseIcon: {
      color: '#FFFFFF',
      fontSize: 16,
      fontWeight: '700',
      lineHeight: 18,
    },
    content: {
      flex: 1,
    },
    contentContainer: {
      padding: 20,
      paddingBottom: 48,
    },
    title: {
      fontSize: 26,
      fontWeight: 'bold',
      color: theme.colors.heading,
      marginBottom: 8,
      textAlign: 'center',
    },
    subtitle: {
      fontSize: 14,
      color: theme.colors.text,
      opacity: 0.85,
      marginBottom: 22,
      textAlign: 'center',
      lineHeight: 20,
    },
    formField: {
      marginBottom: 20,
    },
    label: {
      fontSize: 16,
      fontWeight: '600',
      color: theme.colors.text,
      marginBottom: 8,
    },
    helpText: {
      fontSize: 12,
      color: theme.colors.text,
      opacity: 0.7,
      marginBottom: 8,
      fontStyle: 'italic',
    },
    input: {
      backgroundColor: theme.isDarkMode ? theme.colors.surface : '#F5F5F5',
      borderRadius: 8,
      padding: 12,
      fontSize: 16,
      color: theme.colors.text,
      borderWidth: 1,
      borderColor: theme.colors.border,
    },
    storyDropdownShell: {
      borderRadius: 8,
      borderWidth: 1,
      borderColor: theme.colors.border,
      backgroundColor: theme.isDarkMode ? theme.colors.surface : '#F5F5F5',
      overflow: 'hidden',
      alignSelf: 'stretch',
    },
    storyDropdownInner: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: 14,
      backgroundColor: 'transparent',
      alignSelf: 'stretch',
    },
    dropdownTriggerText: {
      flex: 1,
      fontSize: 16,
      color: theme.colors.text,
      marginRight: 8,
      backgroundColor: 'transparent',
    },
    dropdownChevron: {
      fontSize: 12,
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    descriptionTextArea: {
      minHeight: 220,
      paddingTop: 14,
    },
    dropdownList: {
      marginTop: 8,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: theme.colors.border,
      backgroundColor: theme.isDarkMode ? theme.colors.surface : '#F5F5F5',
      maxHeight: 220,
      overflow: 'hidden',
    },
    dropdownScroll: {
      maxHeight: 220,
    },
    dropdownItem: {
      padding: 12,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: theme.colors.border,
      backgroundColor: 'transparent',
    },
    dropdownItemText: {
      fontSize: 16,
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    uploadButton: {
      backgroundColor: theme.colors.primary || '#d4af37',
      paddingVertical: 14,
      borderRadius: 8,
      alignItems: 'center',
    },
    uploadButtonText: {
      color: theme.isDarkMode ? '#000' : '#000',
      fontSize: 16,
      fontWeight: '600',
    },
    pendingRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginTop: 10,
      paddingVertical: 6,
      paddingHorizontal: 0,
      backgroundColor: 'transparent',
    },
    pendingName: {
      flex: 1,
      fontSize: 14,
      color: theme.colors.text,
      marginRight: 10,
      backgroundColor: 'transparent',
    },
    pendingRemove: {
      fontSize: 18,
      color: '#c44',
      fontWeight: '700',
      paddingHorizontal: 8,
      backgroundColor: 'transparent',
    },
    richToolbar: {
      backgroundColor: theme.isDarkMode ? '#252525' : '#eaeaea',
      borderTopLeftRadius: 8,
      borderTopRightRadius: 8,
      paddingVertical: 6,
    },
    editorShell: {
      borderWidth: 1,
      borderTopWidth: 0,
      borderBottomLeftRadius: 8,
      borderBottomRightRadius: 8,
      overflow: 'hidden',
      minHeight: 220,
    },
    richEditor: {
      minHeight: 220,
      backgroundColor: 'transparent',
    },
    buttonRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      gap: 14,
      marginTop: 10,
    },
    button: {
      flex: 1,
      paddingVertical: 15,
      borderRadius: 8,
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 50,
    },
    clearButton: {
      backgroundColor: '#888',
    },
    clearButtonText: {
      color: '#fff',
      fontSize: 16,
      fontWeight: '600',
    },
    submitButton: {
      backgroundColor: theme.colors.primary || '#d4af37',
    },
    submitButtonText: {
      color: '#000',
      fontSize: 16,
      fontWeight: '600',
    },
  });
